import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Typography, makeStyles, TextField, TextLink } from '@ellucian/react-design-system/core';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { useApi } from '../ApiContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { routes } from '../Helpers/Configuration';
import MFAContext from './MFAContext';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import LoadingButton from '../Common/LoadingButton';

const useStyles = makeStyles((theme) => ({
    title: {
        marginBottom: spacing40
    },
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    wrapper: {
        width: '70%',
    },
    registerContainer: {
        margin: '4rem auto 4rem auto',
    },
    loginContainer: {
        margin: '4rem auto 4rem auto',
        width: '50%',
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: '0 auto'
        }
    },
    codeContainer: {
        marginBottom: spacing40,
    },
    codeTitle: {
        background:theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: spacing40
    },
    textWrapper: {
        width: '100%',
    },
    textField: {
        width: '100%'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    button: {
        marginLeft: spacing40,
        borderRadius: '0',
        border: '0'
    },
    linkContainer: {
        textAlign: 'center',
        marginTop: spacing40,
    },
    [theme.breakpoints.down('sm')]: {
        wrapper: {
            width: '100%'
        }
    },
}));

const OTPVerification = () => {
    const classes = useStyles();
    const api = useApi();
    const history = useHistory();
    const snackbar = useSnackbar();
    const { formatMessage } = useIntl();
    const [OTPState, setOTPState] = useState({ value: '' });
    const { qrCodeInfo, setQRCodeInfo } = useContext(MFAContext);
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (event) => {
        const { value } = event?.target || {};
        const error = '';
        setOTPState({ value, error });
    };

    const handleSubmit = async() => {
        try{
            if (!OTPState?.value?.length) {
                setOTPState({ value: '', error: true });
                snackbar.error(<FormattedMessage id="totpCodeIsInvalid" />);
            }
            else {
                setIsLoading(true);
                const authDetails = await api.mfaAuth.authUser(qrCodeInfo.otpSecret, OTPState.value);
                const { mfaEnabled = true } = authDetails;
                if (!mfaEnabled) {
                    window.location.href = routes.home;
                } else if(authDetails?.data?.isVerified) {
                    setQRCodeInfo({
                        ...qrCodeInfo,
                        backupCode: authDetails?.data?.backupCode
                    });
                    // redirect to recovery read only page
                    history.push(routes.totpRecovery);
                } else if (authDetails?.data?.hasValidated) {
                    window.location.href = routes.home;
                } else {
                    setOTPState({ value: '', error: true });
                    snackbar.error(<FormattedMessage id="totpCodeIsInvalid" />);
                    setIsLoading(false);
                }
            }

        } catch(err) {
            setIsLoading(false);
            console.error(err);
            snackbar.error(<FormattedMessage id="totpUserAuthenticationFailed" />);
        }
    };

    const handleRecoveryCode = async() => {
        history.replace(routes.totpReset);
    };

    return (
        <div className={classes.root} role="contentinfo">
            <div className={classes.wrapper}>
                <div className={!qrCodeInfo.otpVerified ? classes.registerContainer : classes.loginContainer}>
                    <Typography className={classes.title}>
                        <FormattedMessage id="applicationRetrievalAuthenticationCodeInstructions" />
                    </Typography>

                    <div className={classes.codeContainer}>
                        <Typography className={classes.codeTitle}>
                            <HtmlFormattedMessage descriptor={{ id: 'applicationRetrievalEnterAuthenticationCode' }} />
                        </Typography>
                        <div className={classes.textWrapper}>
                            <TextField
                                label={formatMessage({ id: 'totpCode' })}
                                value={OTPState.value}
                                type="text"
                                error = {!!OTPState?.error}
                                onChange={handleChange}
                                className={classes.textField}
                                fullWidth
                                disabled={isLoading}
                            />
                        </div>

                    </div>

                    <div className={classes.buttonContainer}>
                        <LoadingButton
                            color="primary"
                            className={classes.button}
                            onClick={handleSubmit}
                            isLoading={isLoading}
                        >
                            <FormattedMessage id="submit" />
                        </LoadingButton>
                    </div>

                    <div className={classes.linkContainer}>
                        { qrCodeInfo.otpVerified && <TextLink color="secondary" onClick={(e)=>handleRecoveryCode(e)}><FormattedMessage id="totpRecoveryCodeText" /></TextLink>}
                    </div>


                </div>
            </div>
            
        </div>
    );
};

export default OTPVerification;